<template>
  <div class="calico-tiers-table">
    <div
      class="table-scroll"
      role="region"
      aria-label="Tiers Table"
      tabindex="0"
    >
      <table class="table label--medium">
        <thead>
          <tr>
            <th class="col-sticky">
              Risk/Confidence
            </th>
            <th>High</th>
            <th>Medium</th>
            <th class="with-info">
              Low
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.risk"
          >
            <th
              class="col-sticky"
              scope="row"
            >
              {{ row.risk }}
            </th>
            <th
              v-for="(tier, idx) in row.values"
              :key="idx"
              :class="['value-cell', mapTierClass(tier)]"
            >
              <span class="value-text">{{ tier }}</span>
            </th>
          </tr>
        </tbody>
      </table>
    </div>

    <CalicoBanner
      v-if="showNote"
      variant="info"
      :fixed="true"
      class="tiers-note"
    >
      <p class="banner-text">
        {{ banner.before }}
        <span class="emph bold">{{ dialogs.TIERS_TABLE_DATA_CONFIDENCE_LABEL }}</span>
        {{ banner.middle }}
        <b>{{ dialogs.TIERS_TABLE_EMERGING_TEXT }}</b>{{ banner.after }}
      </p>
    </CalicoBanner>
  </div>
</template>

<script>
import CalicoBanner from '@/components/calico/banner/index.vue'
import { BetaScoreDialogs } from '@/enums'

export default {
  name: 'CalicoTiersTable',
  components: { CalicoBanner },
  props: {
    rows: {
      type: Array,
      default: () => ([
        { risk: 'Low',         values: ['Prime','Prime','Emerging'] },
        { risk: 'Medium-Low',  values: ['Growth','Growth','Emerging'] },
        { risk: 'Medium',      values: ['Emerging','Emerging','Emerging'] },
        { risk: 'Medium-High', values: ['Monitor','Monitor','Monitor'] },
        { risk: 'High',        values: ['Caution','Caution','Caution'] }
      ])
    },
    showInfo: {
      type: Boolean, 
      default: true
    },
    showNote: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    dialogs() { return BetaScoreDialogs },
    banner() {
      const t = BetaScoreDialogs.TIERS_TABLE_BANNER_TEMPLATE
      const [before, rest] = t.split('{confidence}')
      const [middle, after] = rest.split('{emerging}')
      return { before, middle, after }
    },
  },
  methods: {
    mapTierClass(tier) {
      switch (tier) {
        case 'Prime': return 'tier--prime'
        case 'Growth': return 'tier--growth'
        case 'Emerging': return 'tier--emerging'
        case 'Monitor': return 'tier--monitor'
        case 'Caution': return 'tier--caution'
        default: return ''
      }
    }
  }
}
</script>

<style scoped lang="scss">
.calico-tiers-table {
  @include stack-space($space-medium);
  align-items: stretch;
  width: 100%;
  
  .table-scroll {
    @include inset-space($space-none);
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border: 1px solid $color-neutral-light;
    border-radius: $radius-small;
    background: $color-white;

    &::-webkit-scrollbar { height: 8px; }
    &::-webkit-scrollbar-thumb { background: $color-neutral-light; border-radius: $radius-small; }
  }

  .table {
    width: 100%;
    min-width: 560px;
    border-collapse: collapse;
    text-align: left;
    th, td { font-weight: inherit; }

    thead th {
      @include inset-space($space-small);
      text-align: left;
      background-color: $color-neutral-lightest;
      white-space: nowrap;
      border-bottom: 1px solid $color-neutral-light;
      border-right: 1px solid $color-neutral-light;

      &.with-info { 
        display: flex; 
        align-items: center; 
        gap: $space-xxsmall; 
      }
    }

    tbody th,
    tbody td {
      @include inset-space($space-small);
      vertical-align: middle;
      background: $color-white;
      border-bottom: 1px solid $color-neutral;
      border-right: 1px solid $color-neutral-light;
    }

    thead th:last-child,
    tbody th:last-child,
    tbody td:last-child { border-right: 0; }

    tbody tr:last-child td,
    tbody tr:last-child th { border-bottom: 0; }

    .col-sticky {
      position: sticky;
      left: 0;
      z-index: 1;
      background-color: $color-neutral-lightest;
      border-right: 1px solid $color-neutral-light;
    }

    tbody th.value-cell {
      text-align: left;
      color: $color-black;
    }

    tbody th.value-cell .value-text {
      display: inline;
      background: transparent;
    }

    tbody th.value-cell.tier--prime    { background: $color-green; }
    tbody th.value-cell.tier--growth   { background: $color-cyan; }
    tbody th.value-cell.tier--emerging { background: $color-yellow; }
    tbody th.value-cell.tier--monitor  { background: $color-orange-light; }
    tbody th.value-cell.tier--caution  { background: $color-pink; }

  }

  .tiers-note .content { display: block; }
  .tiers-note .banner-text { margin: 0; }
  .tiers-note .banner-text .emph { text-decoration: underline; }
}
</style>
